import { DestroyRef, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConsentService } from 'analytics';
import { TranslateService } from 'i18n';

/**
 * Language of the cookie banner, the preference center and the "Cookie settings"
 * links (customer decisions A2/P7, 2026-10-12): English is the master, German is
 * offered too, and a translation that has not been reviewed must never be shown.
 *
 * The backend lists the reviewed languages in `GET /api/t/c` → `banner_languages`
 * (+ `master_language`). If the app's active language is listed, the consent texts
 * follow it; otherwise they are rendered from the master language — its
 * translations are loaded on demand — while the rest of the app stays in the
 * user's language. Same rule as the landing module (wp-consent.js).
 *
 * `ready()` is false until the brand config is known and the shown language's
 * translations are loaded, so the overlay never flashes an unlisted language.
 */
@Injectable({ providedIn: 'root' })
export class ConsentTextsService {
  private consent = inject(ConsentService);
  private translate = inject(TranslateService);

  private readonly activeLang = signal<string>(this.translate.getActiveLang() || 'en');
  /** Languages whose translations are known to be loaded. */
  private readonly loaded = signal<ReadonlySet<string>>(new Set());
  private readonly pending = new Set<string>();

  /** Language the consent texts are shown in; null until the brand config arrived. */
  readonly lang = computed<string | null>(() => {
    if (!this.consent.config()) return null;
    return this.consent.bannerLanguageFor(this.activeLang());
  });

  readonly ready = computed(() => {
    const l = this.lang();
    return l !== null && this.loaded().has(l);
  });

  /** Writing direction of the consent texts — RTL only when the shown language is Arabic. */
  readonly dir = computed<'rtl' | 'ltr'>(() => (this.lang() ?? '').toLowerCase().split('-')[0] === 'ar' ? 'rtl' : 'ltr');

  constructor() {
    this.translate.langChanges.pipe(takeUntilDestroyed(inject(DestroyRef))).subscribe(l => {
      if (l) this.activeLang.set(l);
    });
    // Re-runs when the config arrives (overlay calls consent.ready()) or the language
    // changes. Deliberately does not call ready() itself: an app without the banner
    // must not fetch /api/t/c just because a settings link was rendered.
    effect(() => {
      const l = this.lang();
      if (l !== null) untracked(() => this.ensureLoaded());
    });
  }

  /** Translation of `key` in the consent language ('' until `ready()`). */
  t(key: string, params?: Record<string, unknown>): string {
    const l = this.lang();
    if (l === null || !this.loaded().has(l)) return '';
    return this.translate.translate(key, params, l);
  }

  private ensureLoaded(): void {
    const l = this.lang();
    if (l === null || this.loaded().has(l) || this.pending.has(l)) return;
    this.pending.add(l);
    // Cached by Transloco: the active language (and the fallback 'en') resolve at once.
    this.translate.load(l).subscribe({
      next: () => {
        this.pending.delete(l);
        this.loaded.update(s => new Set(s).add(l));
      },
      error: () => {
        // Last resort: a master language that cannot be loaded leaves the banner hidden
        // rather than showing it in an unreviewed language.
        this.pending.delete(l);
      }
    });
  }
}
